import { useEffect, useState } from 'react'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loading } from '@/components/ui/loading'
import { EmailNotificationPreferences } from '@/components/profile/EmailNotificationPreferences'
import { ArrowLeft, Edit, Save, X, User, Phone, Mail, Building, Key, Copy, Eye, EyeOff } from 'lucide-react'
import { Link } from 'react-router-dom'

export function ProfilePage() {
    const { profile } = useAuth()
    const { hasPermission } = usePermissions()
    const [isEditing, setIsEditing] = useState(false)
    const [editNome, setEditNome] = useState('')
    const [editCognome, setEditCognome] = useState('')
    const [editTelefono, setEditTelefono] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [savingEmailPreference, setSavingEmailPreference] = useState(false)
    const [emailFeedback, setEmailFeedback] = useState<{ message: string; error: boolean } | null>(null)
    const emailPreference = useQuery(api.emails.getMyEmailPreferences)
    const updateEmailPreference = useMutation(api.emails.updateMyEmailPreferences)
    
    // API Key states
    const [showApiKey, setShowApiKey] = useState(false)
    const [generatedApiKey, setGeneratedApiKey] = useState('')
    const [isGeneratingKey, setIsGeneratingKey] = useState(false)

    const updateProfile = useMutation(api.profiles.updateMyProfile)
    const generateApiKey = useMutation(api.apiKeys.generateApiKey)
    const deactivateApiKey = useMutation(api.apiKeys.deactivateApiKey)
    const canUseApiKeys = hasPermission('use_api_keys')
    const sessionInfo = useQuery(api.profiles.getSessionInfo)
    const canManageOwnApiKey =
        canUseApiKeys &&
        sessionInfo !== undefined &&
        !sessionInfo?.impersonating_profile_id
    const currentApiKey = useQuery(api.apiKeys.getCurrentApiKey, canManageOwnApiKey ? {} : 'skip')
    const myApiKey = useQuery(api.apiKeys.getMyApiKey, canManageOwnApiKey ? {} : 'skip')

    useEffect(() => {
        if (profile && window.location.hash === '#email-notifications') {
            document.getElementById('email-notifications')?.scrollIntoView({ block: 'start' })
        }
    }, [profile?._id])

    const handleEmailPreference = async () => {
        if (!emailPreference?.canManage || typeof emailPreference.enabled !== 'boolean' || savingEmailPreference) return
        setSavingEmailPreference(true)
        setEmailFeedback(null)
        try {
            const result = await updateEmailPreference({ enabled: !emailPreference.enabled })
            setEmailFeedback({ message: result.enabled ? 'Notifiche email riattivate.' : 'Notifiche email disattivate. Puoi riattivarle in qualsiasi momento.', error: false })
        } catch {
            setEmailFeedback({ message: 'Non è stato possibile salvare la preferenza. Riprova.', error: true })
        } finally {
            setSavingEmailPreference(false)
        }
    }

    if (!profile) {
        return <Loading />
    }

    const handleEdit = () => {
        setIsEditing(true)
        setEditNome(profile.nome || '')
        setEditCognome(profile.cognome || '')
        setEditTelefono(profile.telefono || '')
    }

    const handleCancel = () => {
        setIsEditing(false)
        setEditNome('')
        setEditCognome('')
        setEditTelefono('')
    }

    const handleSave = async () => {
        if (!editNome.trim() || !editCognome.trim()) {
            alert('Nome e cognome sono obbligatori')
            return
        }

        setIsSaving(true)
        try {
            await updateProfile({
                nome: editNome.trim(),
                cognome: editCognome.trim(),
                telefono: editTelefono.trim() || undefined,
            })
            setIsEditing(false)
        } catch (error) {
            console.error('Error updating profile:', error)
            alert('Errore durante l\'aggiornamento del profilo')
        } finally {
            setIsSaving(false)
        }
    }

    // API Key functions
    const handleGenerateApiKey = async () => {
        if (!canUseApiKeys) {
            alert('Non hai il permesso di generare API key')
            return
        }
        
        if (profile.ruolo === 'clinic_owner' && !profile.company_id) {
            alert('Devi essere associato a un\'azienda per generare una API key')
            return
        }

        setIsGeneratingKey(true)
        try {
            const result = await generateApiKey({})
            setGeneratedApiKey(result.apiKey)
            setShowApiKey(true)
        } catch (error) {
            console.error('Error generating API key:', error)
            alert('Errore durante la generazione della API key')
        } finally {
            setIsGeneratingKey(false)
        }
    }

    const handleDeactivateApiKey = async () => {
        if (!confirm('Sei sicuro di voler disattivare la tua API key? Questa azione non può essere annullata.')) {
            return
        }

        try {
            await deactivateApiKey({})
            setGeneratedApiKey('')
            setShowApiKey(false)
        } catch (error) {
            console.error('Error deactivating API key:', error)
            alert('Errore durante la disattivazione della API key')
        }
    }

    const copyApiKey = () => {
        if (generatedApiKey) {
            navigator.clipboard.writeText(generatedApiKey)
            alert('API key copiata negli appunti!')
        }
    }

    const getRoleLabel = (role: string) => {
        const roleLabels: { [key: string]: string } = {
            'amministratore': 'Amministratore',
            'tecnico_lab': 'Tecnico laboratorio',
            'dentista': 'Dentista',
            'clinic_owner': 'Titolare clinica',
            'aso': 'ASO',
            'staff': 'Personale clinica'
        }
        return roleLabels[role] || role
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <Link to="/">
                        <Button variant="outline" size="sm">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Torna alla Dashboard
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Il Mio Profilo</h1>
                        <p className="text-gray-600 mt-1">
                            Gestisci le tue informazioni personali
                        </p>
                    </div>
                </div>

                {!isEditing && (
                    <Button onClick={handleEdit} className="bg-primary hover:bg-primary/90">
                        <Edit className="w-4 h-4 mr-2" />
                        Modifica Profilo
                    </Button>
                )}
            </div>

            {/* Profile Information */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center">
                        <User className="w-5 h-5 mr-2" />
                        Informazioni Personali
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Nome */}
                        <div>
                            <label className="text-sm font-medium text-gray-500">Nome</label>
                            {isEditing ? (
                                <Input
                                    type="text"
                                    value={editNome}
                                    onChange={(e) => setEditNome(e.target.value)}
                                    className="mt-1"
                                    placeholder="Inserisci il tuo nome"
                                    required
                                />
                            ) : (
                                <div className="mt-1 text-lg">
                                    {profile.nome || 'Non specificato'}
                                </div>
                            )}
                        </div>

                        {/* Cognome */}
                        <div>
                            <label className="text-sm font-medium text-gray-500">Cognome</label>
                            {isEditing ? (
                                <Input
                                    type="text"
                                    value={editCognome}
                                    onChange={(e) => setEditCognome(e.target.value)}
                                    className="mt-1"
                                    placeholder="Inserisci il tuo cognome"
                                    required
                                />
                            ) : (
                                <div className="mt-1 text-lg">
                                    {profile.cognome || 'Non specificato'}
                                </div>
                            )}
                        </div>

                        {/* Telefono */}
                        <div>
                            <label className="text-sm font-medium text-gray-500">Numero di Telefono</label>
                            {isEditing ? (
                                <Input
                                    type="tel"
                                    value={editTelefono}
                                    onChange={(e) => setEditTelefono(e.target.value)}
                                    className="mt-1"
                                    placeholder="Inserisci il tuo numero di telefono"
                                />
                            ) : (
                                <div className="mt-1 text-lg flex items-center">
                                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                    {profile.telefono || 'Non specificato'}
                                </div>
                            )}
                        </div>

                        {/* Email (read-only) */}
                        <div>
                            <label className="text-sm font-medium text-gray-500">Email</label>
                            <div className="mt-1 text-lg flex items-center">
                                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                {profile.email}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                L'email non può essere modificata
                            </p>
                        </div>
                    </div>

                    {/* Role and Company (read-only) */}
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Informazioni Professionali</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium text-gray-500">Ruolo</label>
                                <div className="mt-1 text-lg">
                                    {getRoleLabel(profile.ruolo)}
                                </div>
                            </div>

                            {profile.company_name && (
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Azienda</label>
                                    <div className="mt-1 text-lg flex items-center">
                                        <Building className="w-4 h-4 mr-2 text-gray-400" />
                                        {profile.company_name}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {isEditing && (
                        <div className="flex justify-end space-x-3 pt-6 border-t">
                            <Button
                                variant="outline"
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                <X className="w-4 h-4 mr-2" />
                                Annulla
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="bg-primary hover:bg-primary/90"
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        Salvando...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        Salva Modifiche
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            <EmailNotificationPreferences
                preference={emailPreference}
                impersonating={Boolean(sessionInfo?.impersonating_profile_id)}
                saving={savingEmailPreference}
                feedback={emailFeedback}
                onToggle={handleEmailPreference}
            />

            {/* API Key Management */}
            {canManageOwnApiKey && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <Key className="w-5 h-5 mr-2" />
                            Gestione API Key
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="text-sm text-gray-600">
                            <p>Le API key permettono l'accesso programmatico ai servizi dell'applicazione.</p>
                            <p className="mt-1">
                                <strong>Importante:</strong> Mantieni la tua API key segreta e non condividerla con altri.
                            </p>
                            <p className="mt-1 text-blue-600">
                                <strong>Nota:</strong> l’accesso è riservato ai ruoli autorizzati.
                            </p>
                        </div>

                    {currentApiKey ? (
                        <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-medium text-green-800">API Key Attiva</h4>
                                        <p className="text-sm text-green-600 mt-1">
                                            Creata il: {new Date(currentApiKey.created_at).toLocaleDateString('it-IT')}
                                        </p>
                                        <p className="text-sm text-green-600">
                                            Utilizzi: {currentApiKey.usage_count}
                                            {currentApiKey.last_used_at && (
                                                <span> • Ultimo utilizzo: {new Date(currentApiKey.last_used_at).toLocaleDateString('it-IT')}</span>
                                            )}
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDeactivateApiKey}
                                        className="text-red-600 border-red-200 hover:bg-red-50"
                                    >
                                        Disattiva
                                    </Button>
                                </div>
                            </div>

                            {/* Mostra API key esistente */}
                            {myApiKey && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h4 className="font-medium text-blue-800 mb-2">La tua API Key</h4>
                                    <div className="flex items-center space-x-2">
                                        <Input
                                            type={showApiKey ? "text" : "password"}
                                            value={myApiKey.key}
                                            readOnly
                                            className="font-mono text-sm"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowApiKey(!showApiKey)}
                                        >
                                            {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                navigator.clipboard.writeText(myApiKey.key)
                                                alert('API key copiata negli appunti!')
                                            }}
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-2">
                                        💡 Puoi copiare e utilizzare questa chiave nelle tue applicazioni.
                                    </p>
                                </div>
                            )}

                            {/* Mostra nuova API key generata */}
                            {generatedApiKey && showApiKey && !myApiKey && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h4 className="font-medium text-blue-800 mb-2">La tua nuova API Key</h4>
                                    <div className="flex items-center space-x-2">
                                        <Input
                                            type="text"
                                            value={generatedApiKey}
                                            readOnly
                                            className="font-mono text-sm"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={copyApiKey}
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-2">
                                        ⚠️ Salva questa chiave in un posto sicuro. Ricarica la pagina per vederla sempre.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-6">
                            <Key className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <h4 className="font-medium text-gray-900 mb-2">Nessuna API Key</h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Non hai ancora generato una API key per accedere ai servizi.
                            </p>
                            <Button
                                onClick={handleGenerateApiKey}
                                disabled={isGeneratingKey || (profile.ruolo === 'clinic_owner' && !profile.company_id)}
                                className="bg-primary hover:bg-primary/90"
                            >
                                {isGeneratingKey ? (
                                    <>
                                        <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        Generando...
                                    </>
                                ) : (
                                    <>
                                        <Key className="w-4 h-4 mr-2" />
                                        Genera API Key
                                    </>
                                )}
                            </Button>
                            {profile.ruolo === 'clinic_owner' && !profile.company_id && (
                                <p className="text-xs text-red-600 mt-2">
                                    Devi essere associato a un'azienda per generare una API key
                                </p>
                            )}
                        </div>
                    )}

                    {/* API Usage Documentation */}
                    <div className="border-t pt-4">
                        <h4 className="font-medium text-gray-900 mb-2">Come usare la tua API Key</h4>
                        <div className="text-sm text-gray-600 space-y-2">
                            <p>1. Includi la chiave nell'header Authorization delle tue richieste:</p>
                            <code className="block bg-gray-100 p-2 rounded text-xs font-mono">
                                Authorization: Bearer YOUR_API_KEY
                            </code>
                            <p>2. Endpoint di test disponibile:</p>
                            <code className="block bg-gray-100 p-2 rounded text-xs font-mono">
                                GET {window.location.origin}/service
                            </code>
                        </div>
                    </div>
                </CardContent>
            </Card>
            )}
        </div>
    )
}
